import type { UIViewInjectedProps } from '@uirouter/react';
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo, useState } from 'react';

import { api } from '@/api';
import type { WrappedItemsResult, WrappedResult } from '@/api/types';
import { DrawerForm } from '@/components/drawer-form';
import { counterpartyContractStore } from '@/stores';
import { getTranslatedString } from '@/utils';
import { showError } from '@/utils/common';

import { getFormFields } from './setup';

const entity = 'counterparty-contracts';
const store = counterpartyContractStore;

export const CvExecutionsSelector = observer(({ transition }: UIViewInjectedProps) => {
  const { counterpartyContractId: itemId } = transition.router.globals.params;
  const foundItem = store.items.find(({ id }: any) => id === +itemId);
  const foundItemCvExecutionsIds = useMemo(() => foundItem?.cvExecutions.map(({ id }) => id) || [], [foundItem]);

  const [cvExecutions, setCvExecutions] = useState<cyrian.api.counterpartyContracts.CvExecution[]>([]);
  const [targetExecutions, setTargetExecutions] = useState<number[]>(foundItemCvExecutionsIds);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    if (!foundItem) {
      return;
    }
    setLoading(true);
    api.counterpartyContracts
      .getAvailableCvExecutions(itemId)
      .source.then(({ data }: WrappedItemsResult<cyrian.api.counterpartyContracts.CvExecution[]>) => {
        setTargetExecutions(foundItemCvExecutionsIds);
        setCvExecutions([...data.items, ...(foundItem?.cvExecutions || [])]);
      })
      .then(() => setLoading(false));
  }, [foundItem, itemId, foundItemCvExecutionsIds]);

  const onClose = () => {
    transition.router.stateService.go(`base-layout.${entity}`);
  };

  const title = getTranslatedString('counterparty-contracts.contract-number', { 0: itemId });
  const formFields = getFormFields(cvExecutions, targetExecutions, setTargetExecutions);

  const onSubmit = (values: any) => {
    const { cvExecutions: finalCvExecutions } = values;
    const addDiff = finalCvExecutions.filter((executionId) => !foundItemCvExecutionsIds.includes(executionId));
    const removeDiff = foundItemCvExecutionsIds.filter((executionId) => !finalCvExecutions.includes(executionId));
    const promises = [];

    if (addDiff.length) {
      promises.push(
        api.counterpartyContracts
          .addCvExecutions(itemId, { cvExecutionIds: addDiff })
          .source.then(({ data }: WrappedResult<cyrian.api.counterpartyContracts.CounterpartyContract>) =>
            store.updateItem(data),
          )
          .catch(showError),
      );
    }

    if (removeDiff.length) {
      promises.push(
        api.counterpartyContracts
          .removeCvExecutions(itemId, { cvExecutionIds: removeDiff })
          .source.then(({ data }: WrappedResult<cyrian.api.counterpartyContracts.CounterpartyContract>) =>
            store.updateItem(data),
          )
          .catch(showError),
      );
    }

    return Promise.all(promises);
  };

  const resourceController = {
    create: onSubmit,
  };

  const layout = {
    labelCol: { span: 0 },
    wrapperCol: { span: 24 },
    tailLayout: { wrapperCol: { offset: 0, span: 24 } },
  };

  return (
    <DrawerForm
      {...layout}
      title={title}
      initValues={{}}
      formFields={formFields}
      onClose={onClose}
      resourceController={resourceController}
      loaderCondition={loading || !foundItem}
    />
  );
});
